import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Grid,
  Chip,
  Switch,
  FormControlLabel,
  Avatar,
  Badge,
  Tooltip,
  Breadcrumbs,
  Link,
  CardHeader,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { 
  Delete, 
  Edit, 
  Add, 
  Refresh, 
  AccessTime, 
  CalendarToday, 
  Info,
  NavigateNext,
  Home,
  FilterList,
  MoreVert,
  CheckCircle,
  Cancel,
  Visibility,
  ContentCopy,
  Settings,
  LocalShipping,
  Schedule,
  RocketLaunch,
  EventAvailable,
  Walk,
  Bike,
  Car,
  Truck,
  Close,
  StarBorder,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListDeliveryModelsQuery,
  useAddDeliveryModelMutation,
  useUpdateDeliveryModelMutation,
  useDeleteDeliveryModelMutation,
} from '../../features/admin/deliveryApi.ts';

import {
  DeliveryModel,
  DeliveryModelFormData,
  DELIVERY_TYPE_OPTIONS,
  STATUS_OPTIONS,
  IS_DEFAULT_OPTIONS,
  IS_RESTRICTED_OPTIONS,
} from '../../features/admin/types.ts';

// Shopify-like color palette
const shopifyColors = {
  primary: '#008060',
  primaryLight: '#00a47c',
  primaryDark: '#006e52',
  background: '#f6f6f7',
  surface: '#ffffff',
  border: '#e1e3e5',
  textPrimary: '#202223',
  textSecondary: '#6d7175',
  success: '#008060',
  warning: '#ffc453',
  error: '#d72c0d',
  blue: '#2c6ecb',
  purple: '#5c6ac4',
};

// "18:00:00" -> "18:00" for <input type="time">
const hhmm = (v?: string | null) => (v ? String(v).slice(0, 5) : '');
// "18:00" -> "6 pm", "18:30" -> "6:30 pm" (same as the shop shows)
const clock12 = (v: string) => {
  const [h, m] = v.split(':').map(Number);
  const s = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m ? `${h12}:${String(m).padStart(2, '0')} ${s}` : `${h12} ${s}`;
};

// Promise on specific dates. Server keeps store time as 'YYYY-MM-DD HH:MM:SS';
// <input type="datetime-local"> uses 'YYYY-MM-DDTHH:MM'.
const toLocalInput = (v?: string | null) => (v ? String(v).slice(0, 16).replace(' ', 'T') : '');
const STORE_TZ = 'Asia/Dubai';
// store wall-clock now as 'YYYY-MM-DDTHH:MM'
const storeNow = () => {
  const p = new Intl.DateTimeFormat('en-GB', {
    timeZone: STORE_TZ, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  }).formatToParts(new Date()).reduce((a: any, x) => { a[x.type] = x.value; return a; }, {});
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
};
const wallMs = (v: string) => {
  const [d, t] = v.split('T');
  const [y, mo, da] = d.split('-').map(Number);
  const [h, mi] = (t || '0:0').split(':').map(Number);
  return Date.UTC(y, mo - 1, da, h, mi);
};
const DAY = 86400000;
// "Today" / "Tomorrow" / "Friday, 30 October" (as the shop shows it)
const dayLabel = (v: string) => {
  const diff = Math.round((wallMs(v.slice(0, 10) + 'T00:00') - wallMs(storeNow().slice(0, 10) + 'T00:00')) / DAY);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  return new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(wallMs(v)));
};
// "30 Oct"
const shortDate = (v: string) =>
  new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'short' }).format(new Date(wallMs(v)));
const remainingText = (secs: number) => {
  const mins = Math.max(0, Math.floor(secs / 60));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h >= 24) return `${Math.floor(h / 24)} day${Math.floor(h / 24) === 1 ? '' : 's'} ${h % 24} hr${h % 24 === 1 ? '' : 's'}`;
  return h > 0 ? `${h} hr${h === 1 ? '' : 's'} ${m} min${m === 1 ? '' : 's'}` : `${m} min${m === 1 ? '' : 's'}`;
};
// window text: "6 pm - 9 pm", or "10 pm - 29 Oct, 2 am" over midnight
const windowText = (from: string, to: string) =>
  from.slice(0, 10) === to.slice(0, 10)
    ? `${clock12(from.slice(11, 16))} - ${clock12(to.slice(11, 16))}`
    : `${clock12(from.slice(11, 16))} - ${shortDate(to)}, ${clock12(to.slice(11, 16))}`;
// list chip text for a model
const promiseChip = (m: any): { label: string; ended?: boolean } | null => {
  if (Number(m?.delivery_type) !== 2) return null; // only scheduled delivery has a schedule
  if (m?.cutoff_at && m?.window_start_at && m?.window_end_at) {
    const cut = toLocalInput(m.cutoff_at);
    const from = toLocalInput(m.window_start_at);
    const to = toLocalInput(m.window_end_at);
    const ended = storeNow() >= cut;
    return {
      label: `${ended ? 'Ended · ' : ''}Order by ${shortDate(cut)}, ${clock12(cut.slice(11, 16))} · ${shortDate(from)} ${windowText(from, to)}`,
      ended,
    };
  }
  if (m?.cutoff_time) {
    return {
      label: `Order by ${clock12(hhmm(m.cutoff_time))}${m.window_start && m.window_end ? ` · ${clock12(hhmm(m.window_start))} - ${clock12(hhmm(m.window_end))}` : ''}`,
    };
  }
  return null;
};

const DeliveryManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info'
  });

  // Form related states
  const [formOpen, setFormOpen] = useState(false);
  const [editingModel, setEditingModel] = useState<DeliveryModel | null>(null);
  const [formData, setFormData] = useState<DeliveryModelFormData>({
    model_name: '',
    delivery_type: 0,
    hours: 1,
    days: 1,
    display_text: '',
    status: 1,
    is_default: 0,
    is_restricted: 1,
    delivery_charge: 'FREE',
    cutoff_time: '',
    window_start: '',
    window_end: '',
    cutoff_at: '',
    window_start_at: '',
    window_end_at: '',
  });
  // delivery promise: same rule every day, or one promise on specific dates
  const [promiseMode, setPromiseMode] = useState<'daily' | 'dates'>('daily');
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  // Local state for delivery models
  const [localDeliveryModels, setLocalDeliveryModels] = useState<DeliveryModel[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isProcessingDefault, setIsProcessingDefault] = useState<number | null>(null);
  const [isDeletingId, setIsDeletingId] = useState<number | null>(null);

  // API hooks
  const {
    data,
    isLoading,
    error,
    isError,
    refetch
  } = useListDeliveryModelsQuery();

  const [addDeliveryModel, { isLoading: isAdding }] = useAddDeliveryModelMutation();
  const [updateDeliveryModel, { isLoading: isUpdating }] = useUpdateDeliveryModelMutation();
  const [deleteDeliveryModel, { isLoading: isDeleting }] = useDeleteDeliveryModelMutation();

  // Sort models with default first
  const sortModels = (models: DeliveryModel[]) => {
    return [...models].sort((a, b) => {
      // Default models first
      if (a.is_default === 1 && b.is_default !== 1) return -1;
      if (a.is_default !== 1 && b.is_default === 1) return 1;
      
      // Then sort by name
      const nameA = a.model_name || '';
      const nameB = b.model_name || '';
      return nameA.localeCompare(nameB);
    });
  };

  // Sync API data with local state
  useEffect(() => {
    if (data && !isLoading) {
      const models = data.result || data.data || [];
      console.log('API Data Received:', models);
      
      // Ensure proper mapping of fields
      const mappedModels = models.map((model: DeliveryModel) => ({
        ...model,
        id: model.id || model.model_id,
        model_id: model.model_id || model.id,
        hours: model.hours === null ? 0 : (model.hours || 0),
        days: model.days === null ? 0 : (model.days || 0),
        delivery_charge: model.delivery_charge || 'FREE',
        status: model.status || 1,
        is_default: model.is_default || 0,
        is_restricted: model.is_restricted || 1,
      }));
      
      const sortedModels = sortModels(mappedModels);
      setLocalDeliveryModels(sortedModels);
      setIsRefreshing(false);
      setIsProcessingDefault(null);
    }
  }, [data, isLoading]);

  const isEditing = Boolean(editingModel);
  const isFormLoading = isAdding || isUpdating;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Form handling functions
  const resetForm = () => {
    setFormData({
      model_name: '',
      delivery_type: 0,
      hours: 1,
      days: 1,
      display_text: '',
      status: 1,
      is_default: 0,
      is_restricted: 1,
      delivery_charge: 'FREE',
      cutoff_time: '',
      window_start: '',
      window_end: '',
      cutoff_at: '',
      window_start_at: '',
      window_end_at: '',
    });
    setPromiseMode('daily');
    setFormErrors({});
    setEditingModel(null);
  };

  const handleAddModel = () => {
    resetForm();
    // Pre-check default if there's no existing default
    const hasDefault = localDeliveryModels.some(model => model.is_default === 1);
    setFormData(prev => ({
      ...prev,
      is_default: !hasDefault ? 1 : 0
    }));
    setFormOpen(true);
  };

  const handleEditModel = (model: DeliveryModel) => {
    console.log('Editing model:', model);
    setFormData({
      model_name: model.model_name || '',
      delivery_type: model.delivery_type || 0,
      hours: model.hours === null ? 1 : (model.hours || 1),
      days: model.days === null ? 1 : (model.days || 1),
      display_text: model.display_text || '',
      status: model.status || 1,
      is_default: model.is_default || 0,
      is_restricted: model.is_restricted || 1,
      delivery_charge: model.delivery_charge || 'FREE',
      cutoff_time: hhmm((model as any).cutoff_time),
      window_start: hhmm((model as any).window_start),
      window_end: hhmm((model as any).window_end),
      cutoff_at: toLocalInput((model as any).cutoff_at),
      window_start_at: toLocalInput((model as any).window_start_at),
      window_end_at: toLocalInput((model as any).window_end_at),
      model_id: model.model_id || model.id,
    });
    setPromiseMode((model as any).cutoff_at ? 'dates' : 'daily');
    setEditingModel(model);
    setFormOpen(true);
  };

  const handleSetDefault = async (model: DeliveryModel) => {
    const modelId = model.model_id || model.id;
    
    if (model.is_default === 1) {
      setSnackbar({
        open: true,
        message: 'This delivery option is already the default.',
        severity: 'info'
      });
      return;
    }

    try {
      setIsProcessingDefault(modelId);
      
      // Find the current default model
      const currentDefaultModel = localDeliveryModels.find(m => m.is_default === 1);
      
      // Create batch update data
      const updatePromises = [];
      
      // If there's a current default, prepare to unset it
      if (currentDefaultModel) {
        const currentDefaultId = currentDefaultModel.model_id || currentDefaultModel.id;
        if (currentDefaultId !== modelId) {
          const unsetData = {
            model_id: currentDefaultId,
            model_name: currentDefaultModel.model_name,
            delivery_type: currentDefaultModel.delivery_type,
            hours: currentDefaultModel.hours,
            days: currentDefaultModel.days,
            display_text: currentDefaultModel.display_text,
            status: currentDefaultModel.status,
            is_default: 0,
            is_restricted: currentDefaultModel.is_restricted,
            delivery_charge: currentDefaultModel.delivery_charge || 'FREE',
          };
          
          updatePromises.push(updateDeliveryModel(unsetData).unwrap());
        }
      }
      
      // Prepare data for setting new default
      const setDefaultData = {
        model_id: modelId,
        model_name: model.model_name,
        delivery_type: model.delivery_type,
        hours: model.hours,
        days: model.days,
        display_text: model.display_text,
        status: model.status,
        is_default: 1,
        is_restricted: model.is_restricted,
        delivery_charge: model.delivery_charge || 'FREE',
      };
      
      updatePromises.push(updateDeliveryModel(setDefaultData).unwrap());
      
      // Update local state immediately for better UX
      setLocalDeliveryModels(prev => {
        const updatedModels = prev.map(m => {
          const currentModelId = m.model_id || m.id;
          
          if (currentModelId === modelId) {
            return { ...m, is_default: 1 };
          }
          else if (m.is_default === 1) {
            return { ...m, is_default: 0 };
          }
          return m;
        });
        
        return sortModels(updatedModels);
      });
      
      // Execute all updates
      await Promise.all(updatePromises);
      
      // Re-fetch to ensure everything is in sync
      await refetch();
      
      setSnackbar({
        open: true,
        message: `${model.model_name} is now the default delivery option!`,
        severity: 'success'
      });
      
    } catch (err: any) {
      console.error('Failed to set default delivery model:', err);
      
      // Revert local state changes on error
      await refetch();
      
      let errorMessage = 'Failed to set default delivery option. Please try again.';
      if (err?.data?.message) {
        errorMessage = err.data.message;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setIsProcessingDefault(null);
    }
  };

  const handleFormClose = () => {
    setFormOpen(false);
    resetForm();
    setFormErrors({});
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: Number(value) }));

    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked ? 1 : 0 }));
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.model_name.trim()) {
      newErrors.model_name = 'Delivery name is required';
    }

    if (!formData.display_text.trim()) {
      newErrors.display_text = 'Display text is required';
    }

    if (formData.delivery_type === 0 && (!formData.hours || formData.hours < 1)) {
      newErrors.hours = 'Hours must be at least 1';
    }

    if (formData.delivery_type === 1 && (!formData.days || formData.days < 1)) {
      newErrors.days = 'Days must be at least 1';
    }

    if (!formData.delivery_charge?.trim()) {
      newErrors.delivery_charge = 'Delivery charge is required';
    }

    // delivery window: both times or none, "from" before "to"
    if (formData.delivery_type !== 2) {
      // hour / day basis: no schedule
    } else if (promiseMode === 'daily') {
      const ws = formData.window_start || '';
      const we = formData.window_end || '';
      if (!formData.cutoff_time) newErrors.cutoff_time = 'Choose the daily order cut-off time';
      if (!ws || !we) {
        newErrors.window_end = 'Set the delivery window: "From" and "To"';
      } else if (ws >= we) {
        newErrors.window_end = '"To" must be later than "From"';
      }
    } else {
      // specific dates: all three, cut-off <= from < to, cut-off still ahead
      const cut = formData.cutoff_at || '';
      const from = formData.window_start_at || '';
      const to = formData.window_end_at || '';
      if (!cut) newErrors.cutoff_at = 'Choose the order cut-off date and time';
      else if (!editingModel || cut !== toLocalInput((editingModel as any).cutoff_at)) {
        if (cut <= storeNow()) newErrors.cutoff_at = 'The order cut-off is already past (store time, UAE)';
      }
      if (!from) newErrors.window_start_at = 'Choose when delivery starts';
      if (!to) newErrors.window_end_at = 'Choose when delivery ends';
      if (from && to && from >= to) newErrors.window_end_at = '"Delivery to" must be later than "Delivery from"';
      if (cut && from && cut > from) newErrors.cutoff_at = 'The order cut-off must be before the delivery starts';
    }
    if (Object.keys(newErrors).length) {
      setSnackbar({ open: true, message: Object.values(newErrors)[0], severity: 'error' });
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // FIXED: Submit handler with better debugging
const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  if (!validateForm()) {
    return;
  }

  const scheduledDaily = formData.delivery_type === 2 && promiseMode === 'daily';
  const scheduledDates = formData.delivery_type === 2 && promiseMode === 'dates';

  try {
    const modelId = formData.model_id || editingModel?.model_id || editingModel?.id;
    
    if (isEditing && editingModel && modelId) {
      // Prepare update data with correct fields
      const updateData = {
        model_id: modelId,
        model_name: formData.model_name.trim(),
        delivery_type: formData.delivery_type,
        hours: formData.delivery_type === 0 ? formData.hours : null,
        days: formData.delivery_type === 1 ? formData.days : null,
        display_text: formData.display_text.trim(),
        status: formData.status,
        is_default: formData.is_default,
        is_restricted: formData.is_restricted,
        delivery_charge: formData.delivery_charge?.trim() || 'FREE',
        // delivery promise on the product page ('' = none)
        // schedule: scheduled delivery only ('' = none)
        cutoff_time: scheduledDaily ? formData.cutoff_time || '' : '',
        window_start: scheduledDaily ? formData.window_start || '' : '',
        window_end: scheduledDaily ? formData.window_end || '' : '',
        cutoff_at: scheduledDates ? formData.cutoff_at || '' : '',
        window_start_at: scheduledDates ? formData.window_start_at || '' : '',
        window_end_at: scheduledDates ? formData.window_end_at || '' : '',
      };
      
      console.log('Sending Update Data:', updateData);
      
      // Update the API
      const result = await updateDeliveryModel(updateData).unwrap();
      console.log('Update Response:', result);
      
      if (result?.error === false) {
        // Success - refetch to get latest data from database
        await refetch();
        
        handleFormClose();
        
        setSnackbar({
          open: true,
          message: result?.message || 'Delivery option updated successfully!',
          severity: 'success'
        });
      } else {
        throw new Error(result?.message || 'Update failed');
      }
      
    } else {
      // Creating new model
      const addData = {
        model_name: formData.model_name.trim(),
        delivery_type: formData.delivery_type,
        hours: formData.delivery_type === 0 ? formData.hours : null,
        days: formData.delivery_type === 1 ? formData.days : null,
        display_text: formData.display_text.trim(),
        status: formData.status,
        is_default: formData.is_default,
        is_restricted: formData.is_restricted,
        delivery_charge: formData.delivery_charge?.trim() || 'FREE',
        // delivery promise on the product page ('' = none)
        // schedule: scheduled delivery only ('' = none)
        cutoff_time: scheduledDaily ? formData.cutoff_time || '' : '',
        window_start: scheduledDaily ? formData.window_start || '' : '',
        window_end: scheduledDaily ? formData.window_end || '' : '',
        cutoff_at: scheduledDates ? formData.cutoff_at || '' : '',
        window_start_at: scheduledDates ? formData.window_start_at || '' : '',
        window_end_at: scheduledDates ? formData.window_end_at || '' : '',
      };
      
      console.log('Creating delivery model:', addData);
      
      const result = await addDeliveryModel(addData).unwrap();
      console.log('Create response:', result);
      
      if (result?.error === false) {
        await refetch();
        handleFormClose();
        
        setSnackbar({
          open: true,
          message: result?.message || 'Delivery option created successfully!',
          severity: 'success'
        });
      } else {
        throw new Error(result?.message || 'Creation failed');
      }
    }
    
  } catch (err: any) {
    console.error('Failed to save delivery model:', err);
    
    let errorMessage = `Error ${isEditing ? 'updating' : 'creating'} delivery option. Please try again.`;
    
    if (err?.data?.message) {
      errorMessage = err.data.message;
    } else if (err?.data?.error) {
      errorMessage = typeof err.data.error === 'string' ? err.data.error : 'Validation failed';
    } else if (err?.message) {
      errorMessage = err.message;
    }
    
    setSnackbar({
      open: true,
      message: errorMessage,
      severity: 'error'
    });
    
    // Refetch to restore correct state
    await refetch();
  }
};

  // ✅ FIXED DELETE HANDLER
  const handleDelete = async (id: number) => {
    const modelToDelete = localDeliveryModels.find(model => (model.model_id || model.id) === id);

    if (!modelToDelete) return;

    if (modelToDelete.is_default === 1) {
      setSnackbar({
        open: true,
        message: 'Cannot delete the default delivery option. Please set another option as default first.',
        severity: 'error'
      });
      return;
    }

    if (!window.confirm('Are you sure you want to delete this delivery option?')) {
      return;
    }

    try {
      setIsDeletingId(id);

      // ✅ Call API FIRST — do NOT touch local state yet
      const result = await deleteDeliveryModel({ model_id: id }).unwrap();
      console.log('Delete response:', result);

      // ✅ Check if API returned error: true (e.g. assigned to a product)
      if (result?.error === true) {
        setSnackbar({
          open: true,
          message: result?.message || 'Cannot delete: this delivery option is assigned to a product.',
          severity: 'error'
        });
        // Do NOT modify local state — refetch to stay in sync
        await refetch();
        return;
      }

      // ✅ Only remove from local state after confirmed API success
      setLocalDeliveryModels(prev =>
        prev.filter(model => (model.model_id || model.id) !== id)
      );

      setSnackbar({
        open: true,
        message: result?.message || 'Delivery option deleted successfully!',
        severity: 'success'
      });

      await refetch();

    } catch (err: any) {
      console.error('Failed to delete delivery model:', err);

      // ✅ On error, do NOT remove from UI — item must stay visible
      let errorMessage = 'Cannot delete: this delivery option is assigned to a product.';

      if (err?.data?.message) {
        errorMessage = err.data.message;
      } else if (err?.data?.error) {
        errorMessage = typeof err.data.error === 'string' ? err.data.error : 'Delete operation failed';
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });

      // ✅ Refetch to restore correct state (item should still be there)
      await refetch();

    } finally {
      setIsDeletingId(null);
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    refetch().finally(() => {
      setIsRefreshing(false);
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const renderErrorMessage = () => {
    if (error && 'status' in error) {
      switch (error.status) {
        case 403:
          return 'You don\'t have permission to view delivery options.';
        case 401:
          return 'Please sign in to continue.';
        case 404:
          return 'No delivery options found.';
        case 500:
          return 'Something went wrong. Please try again.';
        default:
          return 'Unable to load delivery options. Please try again.';
      }
    }
    return 'Network error. Please check your connection.';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Updated formatDeliveryCharge function with AED symbol
  const formatDeliveryCharge = (charge: string) => {
    if (!charge || charge === 'FREE') return 'FREE';
    if (charge === '0') return 'FREE';
    if (charge === '0.00') return 'FREE';
    
    const amount = parseFloat(charge).toFixed(2);
    return `AED ${amount}`;
  };

  // Shopify-style status chip
  const getStatusChip = (status: number) => {
    const statusOption = STATUS_OPTIONS.find(opt => opt.value === status);
    return (
      <Chip
        label={statusOption?.label || 'Unknown'}
        size="small"
        variant="outlined"
        sx={{
          borderRadius: '4px',
          fontWeight: 500,
          borderColor: status === 1 ? shopifyColors.success : shopifyColors.textSecondary,
          color: status === 1 ? shopifyColors.success : shopifyColors.textSecondary,
          backgroundColor: status === 1 ? `${shopifyColors.success}08` : 'transparent',
        }}
      />
    );
  };

  // Shopify-style delivery type display
  const getDeliveryTypeDisplay = (deliveryType: number, hours: number | null | undefined, days: number | null | undefined) => {
    const getIcon = () => {
      switch (deliveryType) {
        case 0:
          return <RocketLaunch sx={{ color: shopifyColors.blue, fontSize: 16 }} />;
        case 1:
          return <Schedule sx={{ color: shopifyColors.purple, fontSize: 16 }} />;
        case 2:
          return <EventAvailable sx={{ color: shopifyColors.success, fontSize: 16 }} />;
        default:
          return <LocalShipping sx={{ color: shopifyColors.textSecondary, fontSize: 16 }} />;
      }
    };

    const getText = () => {
      if (deliveryType === 2) return 'Scheduled delivery';
      if (deliveryType === 0) {
        const hoursValue = hours || 0;
        return `${hoursValue} hour${hoursValue > 1 ? 's' : ''}`;
      } else {
        const daysValue = days || 0;
        return `${daysValue} day${daysValue > 1 ? 's' : ''}`;
      }
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {getIcon()}
        <Typography variant="body2" sx={{ color: shopifyColors.textPrimary, fontWeight: 500 }}>
          {getText()}
        </Typography>
      </Box>
    );
  };

  // Get avatar color based on delivery type
  const getAvatarColor = (deliveryType: number) => {
    switch (deliveryType) {
      case 0: return shopifyColors.blue;
      case 1: return shopifyColors.purple;
      case 2: return shopifyColors.success;
      default: return shopifyColors.primary;
    }
  };

  // Get delivery type icon
  const getDeliveryTypeIcon = (deliveryType: number) => {
    switch (deliveryType) {
      case 0: return <RocketLaunch />;
      case 1: return <Schedule />;
      case 2: return <EventAvailable />;
      default: return <LocalShipping />;
    }
  };

  return (
    <Box sx={{ 
      width: '100%',
      height: '100vh',
      overflow: 'hidden',
      position: 'relative',
      display: 'flex', 
      flexDirection: 'column', 
      backgroundColor: shopifyColors.background 
    }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        {/* Drawer for mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          
          {/* Shopify-style Page Header */}
          <Box sx={{ p: 3, pb: 2, backgroundColor: shopifyColors.surface }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="h4" sx={{ 
                  fontWeight: 700, 
                  color: shopifyColors.textPrimary,
                  mb: 1
                }}>
                  Delivery options
                </Typography>
                <Typography variant="body1" sx={{ color: shopifyColors.textSecondary }}>
                  Manage how quickly customers receive their orders
                </Typography>
              </Box>
              
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddModel}
                size="large"
                sx={{
                  backgroundColor: shopifyColors.primary,
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': {
                    backgroundColor: shopifyColors.primaryLight,
                  },
                }}
              >
                Create delivery option
              </Button>
            </Box>
          </Box>

          {/* Main Content Area with Scroll */}
          <Box sx={{ 
            flexGrow: 1, 
            p: 3, 
            pt: 2,
            overflow: 'auto',
            maxHeight: 'calc(100vh - 180px)',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '4px',
              '&:hover': {
                background: '#555',
              },
            },
          }}>
            {/* Search and Filters */}
            <Card sx={{ mb: 3, borderRadius: '8px', border: `1px solid ${shopifyColors.border}` }}>
              <CardContent sx={{ p: '16px !important' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<FilterList />}
                    size="small"
                    sx={{
                      borderRadius: '6px',
                      textTransform: 'none',
                      borderColor: shopifyColors.border,
                      color: shopifyColors.textPrimary,
                    }}
                  >
                    More filters
                  </Button>
                  
                  <Box sx={{ flexGrow: 1 }} />
                  
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleRefresh}
                    disabled={isLoading || isRefreshing}
                    size="small"
                    sx={{
                      borderRadius: '6px',
                      textTransform: 'none',
                      borderColor: shopifyColors.border,
                      color: shopifyColors.textPrimary,
                    }}
                  >
                    {isRefreshing ? 'Refreshing...' : 'Refresh'}
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Results Summary */}
            {!isLoading && !isError && localDeliveryModels.length > 0 && (
              <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 2 }}>
                {localDeliveryModels.length} delivery option{localDeliveryModels.length === 1 ? '' : 's'}
              </Typography>
            )}

            {/* Loading State */}
            {(isLoading || isRefreshing) && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress sx={{ color: shopifyColors.primary }} />
                <Typography sx={{ ml: 2, color: shopifyColors.textSecondary }}>
                  {isRefreshing ? 'Refreshing delivery options...' : 'Loading delivery options...'}
                </Typography>
              </Box>
            )}

            {/* Error State */}
            {isError && !isLoading && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  borderRadius: '6px',
                  border: `1px solid ${shopifyColors.error}20`,
                }}
                action={
                  <Button color="inherit" size="small" onClick={() => refetch()}>
                    Try again
                  </Button>
                }
              >
                {renderErrorMessage()}
              </Alert>
            )}

            {/* Empty State */}
            {!isLoading && !isError && localDeliveryModels.length === 0 && (
              <Card sx={{ textAlign: 'center', py: 8, borderRadius: '8px' }}>
                <LocalShipping sx={{ fontSize: 64, color: shopifyColors.textSecondary, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" sx={{ color: shopifyColors.textPrimary, mb: 1, fontWeight: 600 }}>
                  No delivery options yet
                </Typography>
                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 3 }}>
                  Create your first delivery option to get started
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleAddModel}
                  sx={{
                    backgroundColor: shopifyColors.primary,
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: shopifyColors.primaryLight,
                    },
                  }}
                >
                  Create delivery option
                </Button>
              </Card>
            )}

            {/* Delivery Options List */}
            {!isLoading && localDeliveryModels.length > 0 && (
              <Card sx={{ borderRadius: '8px', border: `1px solid ${shopifyColors.border}`, overflow: 'hidden' }}>
                <List sx={{ p: 0 }}>
                  {localDeliveryModels.map((model: DeliveryModel, index: number) => {
                    const modelId = model.model_id || model.id;
                    const isDefault = model.is_default === 1;
                    const isProcessing = isProcessingDefault === modelId;
                    // ✅ Use dedicated isDeletingId state per row
                    const isCurrentlyDeleting = isDeletingId === modelId;
                    
                    return (
                      <ListItem
                        key={`delivery-${modelId}`}
                        sx={{
                          borderBottom: index < localDeliveryModels.length - 1 ? `1px solid ${shopifyColors.border}` : 'none',
                          '&:hover': {
                            backgroundColor: `${shopifyColors.background}80`,
                          },
                          // ✅ Dim row while deleting is in progress
                          opacity: isCurrentlyDeleting ? 0.5 : 1,
                          transition: 'opacity 0.2s ease',
                        }}
                      >
                        <ListItemIcon>
                          <Avatar
                            sx={{
                              bgcolor: getAvatarColor(model.delivery_type),
                              width: 40,
                              height: 40,
                            }}
                          >
                            {getDeliveryTypeIcon(model.delivery_type)}
                          </Avatar>
                        </ListItemIcon>
                        
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
                                {model.model_name || 'Unnamed'}
                              </Typography>
                              {isDefault && (
                                <Chip 
                                  label="Default" 
                                  size="small" 
                                  sx={{
                                    backgroundColor: shopifyColors.warning,
                                    color: '#000',
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                  }}
                                />
                              )}
                              {getStatusChip(model.status)}
                              {isProcessing && (
                                <CircularProgress size={16} sx={{ ml: 1 }} />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                              {getDeliveryTypeDisplay(model.delivery_type, model.hours, model.days)}
                              {model.display_text && (
                                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                                  {model.display_text}
                                </Typography>
                              )}
                              {(() => {
                                const chip = promiseChip(model);
                                return chip ? (
                                  <Chip
                                    size="small"
                                    icon={<Schedule sx={{ fontSize: 15 }} />}
                                    label={chip.label}
                                    sx={{ height: 22, fontSize: '0.72rem', bgcolor: chip.ended ? '#f1f2f4' : '#f1f8f5', color: chip.ended ? shopifyColors.textSecondary : undefined }}
                                  />
                                ) : null;
                              })()}
                              <Chip 
                                label={formatDeliveryCharge(model.delivery_charge || 'FREE')} 
                                size="small"
                                variant="outlined"
                                sx={{
                                  borderColor: model.delivery_charge === 'FREE' || !model.delivery_charge || model.delivery_charge === '0' ? shopifyColors.success : shopifyColors.primary,
                                  color: model.delivery_charge === 'FREE' || !model.delivery_charge || model.delivery_charge === '0' ? shopifyColors.success : shopifyColors.primary,
                                  backgroundColor: model.delivery_charge === 'FREE' || !model.delivery_charge || model.delivery_charge === '0' ? `${shopifyColors.success}08` : `${shopifyColors.primary}08`,
                                  fontWeight: 500,
                                }}
                              />
                            </Box>
                          }
                        />
                        
                        <ListItemSecondaryAction>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            {!isDefault && (
                              <Tooltip title="Set as Default">
                                <IconButton
                                  onClick={() => handleSetDefault(model)}
                                  size="small"
                                  disabled={isProcessing || isCurrentlyDeleting}
                                  sx={{
                                    color: shopifyColors.warning,
                                    '&:hover': {
                                      backgroundColor: shopifyColors.warning + '20',
                                    }
                                  }}
                                >
                                  <StarBorder />
                                </IconButton>
                              </Tooltip>
                            )}
                            
                            <Tooltip title="Edit">
                              <IconButton
                                onClick={() => handleEditModel(model)}
                                size="small"
                                disabled={isProcessing || isCurrentlyDeleting}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            
                            <Tooltip title={isDefault ? "Cannot delete default option" : "Delete"}>
                              <span>
                                <IconButton
                                  onClick={() => handleDelete(modelId)}
                                  // ✅ Disable only this row's delete button while it's being deleted
                                  disabled={isCurrentlyDeleting || isDefault || isProcessing}
                                  size="small"
                                >
                                  {/* ✅ Show spinner on delete button while deleting */}
                                  {isCurrentlyDeleting
                                    ? <CircularProgress size={18} color="inherit" />
                                    : <Delete />
                                  }
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                </List>
              </Card>
            )}
          </Box>
        </Box>
      </Box>

      {/* Shopify-style Create/Edit Dialog */}
      <Dialog 
        open={formOpen} 
        onClose={handleFormClose} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: '8px',
            border: `1px solid ${shopifyColors.border}`,
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          borderBottom: `1px solid ${shopifyColors.border}`,
          backgroundColor: shopifyColors.background,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
              {isEditing ? 'Edit delivery option' : 'Create delivery option'}
            </Typography>
            <IconButton 
              onClick={handleFormClose} 
              size="small" 
              disabled={isFormLoading}
              sx={{ color: shopifyColors.textSecondary }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Basic Information */}
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  color: shopifyColors.textPrimary, 
                  fontWeight: 600,
                  mb: 1 
                }}>
                  Basic information
                </Typography>
                
                <TextField
                  name="model_name"
                  label="Delivery name"
                  value={formData.model_name}
                  onChange={handleInputChange}
                  error={!!formErrors.model_name}
                  helperText={formErrors.model_name}
                  fullWidth
                  required
                  placeholder="e.g., Express delivery, Standard shipping"
                  disabled={isFormLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                    }
                  }}
                />
              </Box>

              <Divider />

              {/* Delivery Type Selection */}
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  color: shopifyColors.textPrimary, 
                  fontWeight: 600,
                  mb: 2 
                }}>
                  Delivery speed
                </Typography>
                
                <FormControl fullWidth required>
                  <InputLabel>Delivery type</InputLabel>
                  <Select
                    name="delivery_type"
                    value={formData.delivery_type}
                    onChange={handleSelectChange}
                    label="Delivery type"
                    disabled={isFormLoading}
                    sx={{ borderRadius: '6px' }}
                  >
                    {DELIVERY_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {option.value === 0 ? (
                            <RocketLaunch sx={{ color: shopifyColors.blue }} />
                          ) : option.value === 2 ? (
                            <EventAvailable sx={{ color: shopifyColors.success }} />
                          ) : (
                            <Schedule sx={{ color: shopifyColors.purple }} />
                          )}
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {option.label}
                            </Typography>
                            <Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>
                              {option.description}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Delivery Time and Charge */}
              <Grid container spacing={2}>
                {formData.delivery_type === 0 && (
                  <>
                    <Grid item xs={6}>
                      <TextField
                        name="hours"
                        label="Maximum hours"
                        type="number"
                        value={formData.hours}
                        onChange={handleInputChange}
                        error={!!formErrors.hours}
                        helperText={formErrors.hours || "Maximum hours for delivery"}
                        fullWidth
                        required
                        disabled={isFormLoading}
                        inputProps={{ min: 1, max: 168 }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        name="delivery_charge"
                        label="Delivery charge (AED)"
                        value={formData.delivery_charge}
                        onChange={handleInputChange}
                        error={!!formErrors.delivery_charge}
                        helperText={formErrors.delivery_charge || "Enter 0 for FREE delivery, or amount in AED"}
                        fullWidth
                        required
                        disabled={isFormLoading}
                        placeholder="0.00"
                        InputProps={{
                          startAdornment: <Typography sx={{ mr: 1, color: shopifyColors.textSecondary }}>AED</Typography>,
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                          }
                        }}
                      />
                    </Grid>
                  </>
                )}

                {formData.delivery_type === 1 && (
                  <>
                    <Grid item xs={6}>
                      <TextField
                        name="days"
                        label="Maximum days"
                        type="number"
                        value={formData.days}
                        onChange={handleInputChange}
                        error={!!formErrors.days}
                        helperText={formErrors.days || "Maximum days for delivery"}
                        fullWidth
                        required
                        disabled={isFormLoading}
                        inputProps={{ min: 1, max: 365 }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        name="delivery_charge"
                        label="Delivery charge (AED)"
                        value={formData.delivery_charge}
                        onChange={handleInputChange}
                        error={!!formErrors.delivery_charge}
                        helperText={formErrors.delivery_charge || "Enter 0 for FREE delivery, or amount in AED"}
                        fullWidth
                        required
                        disabled={isFormLoading}
                        placeholder="0.00"
                        InputProps={{
                          startAdornment: <Typography sx={{ mr: 1, color: shopifyColors.textSecondary }}>AED</Typography>,
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                          }
                        }}
                      />
                    </Grid>
                  </>
                )}
                {formData.delivery_type === 2 && (
                  <>
                    <Grid item xs={6}>
                      <TextField
                        name="delivery_charge"
                        label="Delivery charge (AED)"
                        value={formData.delivery_charge}
                        onChange={handleInputChange}
                        error={!!formErrors.delivery_charge}
                        helperText={formErrors.delivery_charge || "Enter 0 for FREE delivery, or amount in AED"}
                        fullWidth
                        required
                        disabled={isFormLoading}
                        placeholder="0.00"
                        InputProps={{
                          startAdornment: <Typography sx={{ mr: 1, color: shopifyColors.textSecondary }}>AED</Typography>,
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                          }
                        }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>

              {/* Display Text */}
              <Box>
                <TextField
                  name="display_text"
                  label="Customer display text"
                  value={formData.display_text}
                  onChange={handleInputChange}
                  error={!!formErrors.display_text}
                  helperText={formErrors.display_text || "This text will be shown to customers at checkout"}
                  fullWidth
                  required
                  multiline
                  rows={2}
                  disabled={isFormLoading}
                  placeholder="e.g., Get it in 2 hours, Standard 3-5 business days"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                    }
                  }}
                />
              </Box>

              {/* Scheduled delivery: order cut-off + delivery window (shown under "In Stock") */}
              {formData.delivery_type === 2 && (
              <Box>
                <Typography variant="subtitle2" sx={{ color: shopifyColors.textPrimary, fontWeight: 600, mb: 0.5 }}>
                  Delivery schedule
                </Typography>
                <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, display: 'block', mb: 1.5 }}>
                  Customers order before the cut-off and get it in the delivery window. The product page shows e.g.
                  "FREE delivery Today 6 pm - 10 pm. Order within 3 hrs 38 mins". Times are store time (UAE).
                </Typography>
                <ToggleButtonGroup
                  value={promiseMode}
                  exclusive
                  size="small"
                  disabled={isFormLoading}
                  onChange={(_, v) => v && setPromiseMode(v)}
                  sx={{
                    mb: 1,
                    '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, px: 2.25, color: shopifyColors.textSecondary, gap: 0.75 },
                    '& .Mui-selected': { bgcolor: `${shopifyColors.primary} !important`, color: 'white !important' },
                  }}
                >
                  <ToggleButton value="daily"><AccessTime sx={{ fontSize: 17 }} />Every day</ToggleButton>
                  <ToggleButton value="dates"><CalendarToday sx={{ fontSize: 16 }} />Specific dates</ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, display: 'block', mb: 2 }}>
                  {promiseMode === 'daily'
                    ? 'Same times every day. After the cut-off the promise moves to the next day.'
                    : 'One promise with exact dates, e.g. for a festival. The line is hidden once the cut-off has passed.'}
                </Typography>
                {promiseMode === 'daily' ? (
                <>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      type="time"
                      name="cutoff_time"
                      label="Order cut-off"
                      value={formData.cutoff_time || ''}
                      onChange={handleInputChange}
                      fullWidth
                      size="small"
                      disabled={isFormLoading}
                      InputLabelProps={{ shrink: true }}
                      error={!!formErrors.cutoff_time}
                      helperText={formErrors.cutoff_time || 'Orders before this time come today'}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField
                      type="time"
                      name="window_start"
                      label="Delivery from"
                      value={formData.window_start || ''}
                      onChange={handleInputChange}
                      fullWidth
                      size="small"
                      disabled={isFormLoading}
                      InputLabelProps={{ shrink: true }}
                      helperText="Delivery window start"
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField
                      type="time"
                      name="window_end"
                      label="Delivery to"
                      value={formData.window_end || ''}
                      onChange={handleInputChange}
                      fullWidth
                      size="small"
                      disabled={isFormLoading}
                      InputLabelProps={{ shrink: true }}
                      error={!!formErrors.window_end}
                      helperText={formErrors.window_end || ' '}
                    />
                  </Grid>
                </Grid>
                {!!formData.cutoff_time && (
                  <Box sx={{ mt: 1.5, p: 1.5, borderRadius: '8px', bgcolor: '#f1f8f5', border: `1px solid ${shopifyColors.border}` }}>
                    <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, display: 'block' }}>
                      Preview
                    </Typography>
                    <Typography variant="body2" sx={{ color: shopifyColors.textPrimary }}>
                      <b>
                        {String(formData.delivery_charge || '').toUpperCase() === 'FREE' || Number(formData.delivery_charge) === 0 ? 'FREE delivery' : 'Delivery'}
                        {storeNow().slice(11) >= (formData.cutoff_time || '') ? ' Tomorrow' : ' Today'}
                        {formData.window_start && formData.window_end ? ` ${clock12(formData.window_start)} - ${clock12(formData.window_end)}` : ''}
                      </b>
                      . Order within <span style={{ color: shopifyColors.primary, fontWeight: 600 }}>… hrs … mins</span> (before {clock12(formData.cutoff_time)})
                    </Typography>
                  </Box>
                )}
                </>
                ) : (
                <>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      type="datetime-local"
                      name="cutoff_at"
                      label="Order cut-off"
                      value={formData.cutoff_at || ''}
                      onChange={handleInputChange}
                      fullWidth
                      size="small"
                      disabled={isFormLoading}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: storeNow() }}
                      error={!!formErrors.cutoff_at}
                      helperText={formErrors.cutoff_at || 'Last date and time to order'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      type="datetime-local"
                      name="window_start_at"
                      label="Delivery from"
                      value={formData.window_start_at || ''}
                      onChange={handleInputChange}
                      fullWidth
                      size="small"
                      disabled={isFormLoading}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: formData.cutoff_at || storeNow() }}
                      error={!!formErrors.window_start_at}
                      helperText={formErrors.window_start_at || 'Delivery date and start time'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      type="datetime-local"
                      name="window_end_at"
                      label="Delivery to"
                      value={formData.window_end_at || ''}
                      onChange={handleInputChange}
                      fullWidth
                      size="small"
                      disabled={isFormLoading}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: formData.window_start_at || storeNow() }}
                      error={!!formErrors.window_end_at}
                      helperText={formErrors.window_end_at || 'Delivery end'}
                    />
                  </Grid>
                </Grid>
                {!!(formData.cutoff_at && formData.window_start_at && formData.window_end_at) && (
                  <Box sx={{ mt: 1.5, p: 1.5, borderRadius: '8px', bgcolor: '#f1f8f5', border: `1px solid ${shopifyColors.border}` }}>
                    <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, display: 'block' }}>
                      Preview (as customers see it now)
                    </Typography>
                    {String(formData.cutoff_at || '') <= storeNow() ? (
                      <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                        The cut-off has passed, so the line is not shown.
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ color: shopifyColors.textPrimary }}>
                        <b>
                          {String(formData.delivery_charge || '').toUpperCase() === 'FREE' || Number(formData.delivery_charge) === 0 ? 'FREE delivery' : 'Delivery'}
                          {' '}{dayLabel(String(formData.window_start_at || ''))}
                          {dayLabel(String(formData.window_start_at || '')) === 'Today' || dayLabel(String(formData.window_start_at || '')) === 'Tomorrow' ? ' ' : ', '}
                          {String(formData.window_start_at || '') < String(formData.window_end_at || '') ? windowText(String(formData.window_start_at || ''), String(formData.window_end_at || '')) : ''}
                        </b>
                        . Order within{' '}
                        <span style={{ color: shopifyColors.primary, fontWeight: 600 }}>
                          {remainingText((wallMs(String(formData.cutoff_at || '')) - wallMs(storeNow())) / 1000)}
                        </span>
                        {' '}(before {shortDate(String(formData.cutoff_at || ''))}, {clock12(String(formData.cutoff_at || '').slice(11, 16))})
                      </Typography>
                    )}
                  </Box>
                )}
                </>
                )}
              </Box>
              )}

              <Divider />

              {/* Settings */}
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  color: shopifyColors.textPrimary, 
                  fontWeight: 600,
                  mb: 2 
                }}>
                  Settings
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.status === 1}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          status: e.target.checked ? 1 : 0 
                        }))}
                        color="success"
                        disabled={isFormLoading}
                      />
                    }
                    label="Active"
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_default === 1}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          is_default: e.target.checked ? 1 : 0 
                        }))}
                        color="warning"
                        disabled={isFormLoading}
                      />
                    }
                    label="Set as default delivery option"
                  />

                  <FormControl fullWidth>
                    <InputLabel>Customer access</InputLabel>
                    <Select
                      name="is_restricted"
                      value={formData.is_restricted}
                      onChange={handleSelectChange}
                      label="Customer access"
                      disabled={isFormLoading}
                      sx={{ borderRadius: '6px' }}
                    >
                      {IS_RESTRICTED_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>Choose who can use this delivery option</FormHelperText>
                  </FormControl>
                </Box>
              </Box>
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ 
            p: 3, 
            pt: 2, 
            gap: 2,
            borderTop: `1px solid ${shopifyColors.border}`,
          }}>
            <Button 
              onClick={handleFormClose} 
              disabled={isFormLoading}
              variant="outlined"
              sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: shopifyColors.border,
                color: '#eeeeee',
                bgcolor: '#f44336',
              }}
            >
              Close  
            </Button>
            <Button 
              onClick={handleFormClose} 
              disabled={isFormLoading}
              variant="outlined"
              sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: shopifyColors.border,
                color: '#eeeeee',
                bgcolor: '#F78E12',
              }}
            >
              Cancel  
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isFormLoading || !formData.model_name?.trim() || !formData.display_text?.trim() || !formData.delivery_charge?.trim()}
              sx={{
                backgroundColor: shopifyColors.primary,
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                '&:hover': {
                  backgroundColor: shopifyColors.primaryLight,
                },
              }}
            >
              {isFormLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  {isEditing ? 'Saving...' : 'Creating...'}
                </Box>
              ) : (
                isEditing ? 'Save changes' : 'Create delivery option'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            borderRadius: '6px',
            alignItems: 'center',
          }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeliveryManagement;